// Which pyramid level OpenLayers draws when the zoom falls between two.
//
// Its own rule takes the level nearest in resolution, so up to halfway it draws
// the coarser level stretched. Across the 4x steps many scanner files hold
// (40x, 10x, 2.5x) that is the 10x level drawn up to 1.6 times its size
// between 10x and 16x: soft enough that a 40x slide was taken for a badly
// focused scan (prod, 2026-10-09).
//
// Shrinking the finer level is sharp only up to 2x. The slide's tiles are
// sampled bilinearly (smoothTiles turns it on; OpenLayers' own default is the
// nearest pixel) with no mipmaps (ol/webgl/TileTexture): at 2x that averages
// every pixel, past it pixels are skipped and the slide turns grainy and
// shimmers as it moves. So: the finer level while it is shrunk by no more
// than MAX_SHRINK, which on a pyramid of 2x steps is always (the converter
// writes every one since 2026-10-09). Across a wider step neither is clean, and
// the coarser level takes over once it is stretched by no more than MAX_STRETCH.
//
// Between those, the finer level is drawn shrunk further, but only as far as
// the pane's tile cache can hold the frame: a frame's tiles grow with the
// square of the shrink, and OpenLayers' renderer expires its cache after every
// frame with no regard for the tiles it has just drawn, so a frame larger than
// the cache is evicted and fetched again every frame and never settles. The
// engine gives the limit (gpuCalibration.affordableShrink); past it the
// coarser level is drawn stretched, soft but calm. HARD_SHRINK bounds it
// whatever the budget: at 4x every fourth pixel is sampled, and a frame is
// 16 screens of tiles.
//
// All of that is measured in the pixels OpenLayers chooses levels by, and
// left to itself that is CSS pixels: a level drawn 1:1 is drawn one level
// pixel per CSS pixel, which on a dense screen is stretched by the screen's
// density (2x on a Retina display) however the slide is zoomed. A viewer that
// chooses per device pixel (OpenSeadragon, through its pixelDensityRatio)
// draws the level finer than that at every zoom, and looked sharper than this
// one on the same slide (compared side by side, 2026-10-11).
// scaleLevelChoice has each tile grid choose its level for the view's
// resolution divided by a scale, the screen's density as far as the tile
// budget affords it (gpuCalibration.affordableLevelScale): the shrink and
// stretch above are then in device pixels, and a frame holds the square of
// the scale more tiles.

/** The most the finer level is shrunk before the coarser one is considered. */
export const MAX_SHRINK = 2;
/** Across a step wider than 2x, the most the coarser level is stretched. */
export const MAX_STRETCH = 1.25;
/** The most the finer level is ever shrunk, however large the tile cache. */
export const HARD_SHRINK = 4;
/** The most device pixels per CSS pixel levels are chosen by, whatever the
 *  screen's density: at 3 a frame already holds nine times the tiles. */
export const MAX_LEVEL_SCALE = 3;

/**
 * An OpenLayers NearestDirectionFunction (ol/array linearFindNearest): given
 * the view's resolution and the resolutions of the two levels around it (the
 * larger is the coarser), above 0 for the coarser level, otherwise the finer.
 * `shrinkLimit` is how far the finer level may be shrunk where neither level
 * is clean, MAX_SHRINK to HARD_SHRINK.
 */
export function chooseLevel(resolution: number, coarser: number, finer: number, shrinkLimit = MAX_SHRINK): number {
  const shrink = resolution / finer;
  if (shrink <= MAX_SHRINK) return -1;
  if (coarser / resolution <= MAX_STRETCH) return 1;
  return shrink <= Math.min(HARD_SHRINK, shrinkLimit) ? -1 : 1;
}

/** An OpenLayers layer, as far as this needs it: a tile source has a tile grid. */
export interface TileLayer {
  getSource?(): unknown;
}

/** An OpenLayers tile grid, as far as this needs it: the level for a resolution. */
export interface LevelGrid {
  getZForResolution(resolution: number, direction?: unknown): number;
}

const SCALED = Symbol.for('tileriver.levelScale');

/**
 * Have a tile grid choose its level for the view's resolution divided by
 * `scale()` (device pixels per CSS pixel to choose by; 1 is OpenLayers' own
 * choice), read at every call. Every level lookup OpenLayers makes for a
 * frame (the level to draw, the levels to preload, the level a pixel is read
 * from) goes through this one method of the grid, so wrapping it moves the
 * whole choice without touching the grid's geometry: a tile still covers the
 * same part of the slide, it is just drawn smaller. A grid is wrapped once.
 */
export function scaleLevelChoice(grid: LevelGrid, scale: () => number): void {
  const marked = grid as LevelGrid & { [SCALED]?: true };
  if (marked[SCALED]) return;
  const original = grid.getZForResolution;
  grid.getZForResolution = function (this: LevelGrid, resolution: number, direction?: unknown): number {
    const s = scale();
    return original.call(this, s > 0 ? resolution / s : resolution, direction);
  };
  marked[SCALED] = true;
}

/** The parts of an OpenLayers map this needs: its layer collection. */
export interface TileMap {
  getLayers(): {
    getArray(): TileLayer[];
    on(type: 'add', listener: (e: { element: TileLayer }) => void): unknown;
    un(type: 'add', listener: (e: { element: TileLayer }) => void): unknown;
  };
}

/** An OpenLayers tile source, as far as the watchers here need it. */
export interface TileSource {
  getTileGrid(): LevelGrid | null | undefined;
  zDirection?: unknown;
  setLoader?: (loader: (...args: unknown[]) => Promise<unknown>) => void;
  /** What OpenLayers copies into every new tile (tileFade sets the fade here). */
  tileOptions?: { transition?: number };
}

/**
 * Apply `apply` to every tile source on the map, now and added later (a
 * mask's layers come with the mask); a layer without a tile grid is left
 * alone. Returns how many tile sources are on the map now, and a function
 * that stops watching for new layers.
 */
export function watchTileSources(map: TileMap, apply: (source: TileSource) => void): { sources: number; stop: () => void } {
  const tileSourceOf = (layer: TileLayer): TileSource | null => {
    const source = layer.getSource?.() as TileSource | null | undefined;
    return source && typeof source.getTileGrid === 'function' ? source : null;
  };
  const layers = map.getLayers();
  let sources = 0;
  for (const layer of layers.getArray()) {
    const source = tileSourceOf(layer);
    if (!source) continue;
    apply(source);
    sources++;
  }
  const onAdd = (e: { element: TileLayer }) => {
    const source = tileSourceOf(e.element);
    if (source) apply(source);
  };
  layers.on('add', onAdd);
  return { sources, stop: () => void layers.un('add', onAdd) };
}

/**
 * Have every tile layer on the map, now and added later, choose its level by
 * chooseLevel, for the view's resolution divided by `levelScale()`
 * (scaleLevelChoice): OpenLayers reads a source's zDirection at every frame.
 * `shrinkLimit` and `levelScale` are read at every frame too, so they can
 * follow the tile budget. Returns what watchTileSources returns.
 */
export function preferSharpLevels(
  map: TileMap,
  shrinkLimit: () => number,
  levelScale: () => number = () => 1,
): { sources: number; stop: () => void } {
  const direction = (resolution: number, coarser: number, finer: number) =>
    chooseLevel(resolution, coarser, finer, shrinkLimit());
  return watchTileSources(map, (source) => {
    source.zDirection = direction;
    const grid = source.getTileGrid();
    if (grid && typeof grid.getZForResolution === 'function') scaleLevelChoice(grid, levelScale);
  });
}
